import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector, useStore } from "react-redux";
import NumberFormat from "react-number-format";

import { Alert, Slide } from "@mui/material";

import { Row } from "../components/Row.tsx";
import { Total } from "../components/Total.tsx";

import { setSpendDetailsRows, setTotals, removeSpendDetailsRow, } from "../Store/roiStore.ts";

import { getTotal } from "../utils/index.ts";
import {
  CUSTOMER_SITUATION,
  isExistingCustomerMode,
  resolveCustomerSituation,
  sumFacilityScales,
} from "../utils/customerSituation.ts";
import "../styles/pages/spend-details.scss";

// Facilities enter an *average daily* call-down count; multiply by this to show
// the equivalent monthly total in the summary row.
const DAYS_PER_MONTH = 30;

export const SpendDetails = () => {
  const { dispatch } = useStore();
  const spendDetailRows = useSelector(
    (state) => state.inputFields.spendDetailsRows
  );
  const totalsInStore = useSelector((state) => state.inputFields.totals);
  const client = useSelector((state) => state.inputFields.client);
  const isSameDayIncluded = useSelector(
    (state) => !state.inputFields.sameDay?.isSameDayOnly &&
      (state.inputFields.client?.currentParSolutions ?? []).includes("PAR SameDay")
  );
  const situation = resolveCustomerSituation(client);
  const existingMode = isExistingCustomerMode(situation);
  const scaleSplitTotals = sumFacilityScales(spendDetailRows, situation);

  const npsr = Number(client?.annualNetPatientServiceRevenue ?? 0);
  const totalSupplySpend = npsr * 0.13;
  const medsurg = totalSupplySpend * 0.20;
  const directPurchases = totalSupplySpend * 0.60;
  const billOnly = totalSupplySpend * 0.20;
  // Estimated addressable spend: Medsurg + Direct Purchases, plus Bill-Only if SameDay is in scope
  const estimatedAddressableSpend = isSameDayIncluded
    ? totalSupplySpend
    : medsurg + directPurchases;

  const rowKeys = Object.keys(spendDetailRows);
  const [monthlyCallDownsTotal, setMonthlyCallDownsTotal] = useState(
    totalsInStore?.["annualCallDowns"] ?? 0
  );
  const [annualMedsurgSpendTotal, setAnnualMedsurgSpendTotal] = useState(
    totalsInStore?.["annualMedsurgSpend"] ?? 0
  );
  const [annualDirectPurchaseSpendTotal, setAnnualDirectPurchaseSpendTotal] = useState(
    totalsInStore?.["annualDirectPurchaseSpend"] ?? 0
  );
  // annualSupplySpendTotal = Medsurg + Direct Purchases (combined, used for downstream calc)
  const [annualSupplySpendTotal, setAnnualSupplySpendTotal] = useState(
    totalsInStore?.["annualSpendDetails"] ?? 0
  );
  const [annualBillOnlySpendTotal, setAnnualBillOnlySpendTotal] = useState(
    totalsInStore?.["annualBillOnlySpend"] ?? 0
  );
  const [scaleCountTotal, setScaleCountTotal] = useState(
    totalsInStore?.["scaleCount"] ?? 0
  );


  const addRow = (rowId) => {
    setRows((prevState) => [
      ...prevState,
      <Row
        rowId={prevState.length + 1}
        onAddRow={addRow}
        onRemoveRow={removeRow}
      />,
    ]);
  };

  const removeRow = (rowId: number) => {
    setRows((prevState) => [
      ...prevState.filter((item, index) => index !== rowId - 1),
    ]);

    dispatch(
      removeSpendDetailsRow({
        rowId: rowId,
      })
    );
  };

  const [rows, setRows] = useState(
    rowKeys.map((key, index) => {
      return (
        <Row rowId={index + 1} onAddRow={addRow} onRemoveRow={removeRow} />
      );
    })
  );

  useEffect(() => {
    if (spendDetailRows) {
      const medsurgTotal = getTotal("Annual Medsurg Spend", spendDetailRows);
      const directPurchaseTotal = getTotal("Annual Direct Purchase Spend", spendDetailRows);
      // Backward compat: old ROIs stored a single "Annual Supply Spend" — use it as fallback
      const legacySupplyTotal = getTotal("Annual Supply Spend", spendDetailRows);
      const supplySpendTotal = (medsurgTotal + directPurchaseTotal) || legacySupplyTotal;

      const billOnlySpendTotal = getTotal("Annual Bill Only Spend", spendDetailRows);
      const callDownCostTotal = getTotal("Estimated Monthly Call Downs", spendDetailRows);
      const scaleCountTotal = existingMode
        ? scaleSplitTotals.totalScales
        : getTotal("Scale Count", spendDetailRows);

      setAnnualMedsurgSpendTotal(medsurgTotal);
      setAnnualDirectPurchaseSpendTotal(directPurchaseTotal);
      setAnnualSupplySpendTotal(supplySpendTotal);
      setAnnualBillOnlySpendTotal(billOnlySpendTotal);
      setMonthlyCallDownsTotal(callDownCostTotal);
      setScaleCountTotal(scaleCountTotal);

      dispatch(setTotals({ name: "annualMedsurgSpend", value: medsurgTotal }));
      dispatch(setTotals({ name: "annualDirectPurchaseSpend", value: directPurchaseTotal }));

      if (supplySpendTotal)
        dispatch(
          setTotals({
            name: "annualSpendDetails",
            value: supplySpendTotal,
          })
        );

      if (billOnlySpendTotal)
        dispatch(
          setTotals({
            name: "annualBillOnlySpend",
            value: billOnlySpendTotal,
          })
        );

      if (callDownCostTotal)
        dispatch(
          setTotals({
            name: "monthlyCallDowns",
            value: callDownCostTotal,
          })
        );

      dispatch(
        setTotals({
          name: "scaleCount",
          value: scaleCountTotal || 0,
        })
      );
      dispatch(setTotals({ name: "newScaleCount", value: scaleSplitTotals.newScales }));
      dispatch(setTotals({ name: "existingScaleCount", value: scaleSplitTotals.existingScales }));
    }
  }, [spendDetailRows, rows, existingMode, scaleSplitTotals.totalScales, scaleSplitTotals.newScales, scaleSplitTotals.existingScales]);

  // Adding a new row
  useEffect(() => {
    dispatch(
      setSpendDetailsRows({
        rowId: rows.length,
        name: "",
        value: "",
      })
    );
  }, [rows.length]);

  return (
    <Slide direction="right" in={true} mountOnEnter unmountOnExit>
      <div className="spend-details-page">
        <h1>Spend Details by Facility</h1>
        <p>
          Please enter spend details below. You can add or remove lines at any
          time
        </p>
        {situation === CUSTOMER_SITUATION.PAR360_ADDON && (
          <Alert severity="info" sx={{ textAlign: "left", marginBottom: "16px" }}>
            Incremental PAR 360 analysis: include the already-scaled hospitals in
            this deal. Scale count is the installed base (for context). Spend,
            call-downs, and inventory-system fees should reflect remaining
            opportunity after the scales they already have — not original
            pre-scale rates.
          </Alert>
        )}
        {situation === CUSTOMER_SITUATION.MIXED && (
          <Alert severity="info" sx={{ textAlign: "left", marginBottom: "16px" }}>
            Tag each hospital Already scaled or Unscaled / new. New sites get a
            full scale ROI. Already-scaled sites contribute PAR 360 value only.
          </Alert>
        )}
        {situation === CUSTOMER_SITUATION.SCALE_EXPANSION && (
          <Alert severity="info" sx={{ textAlign: "left", marginBottom: "16px" }}>
            Enter existing vs new scales. Only new scales create new labor /
            order-line savings. Put hardware purchase in CAPEX on Investment.
          </Alert>
        )}

        <div className="spend-details-reference">
          <p className="spend-details-reference-text">
            For reference: Given an IDN NPSR of{" "}
            <NumberFormat
              value={npsr}
              displayType={"text"}
              thousandSeparator={true}
              prefix={"$"}
            />
            , PAR estimates that the client's total IDN supply spend equates to
            approximately 13% of NPSR, or{" "}
            <NumberFormat
              value={Math.round(totalSupplySpend)}
              displayType={"text"}
              thousandSeparator={true}
              prefix={"$"}
            />
            , with the following breakdown:
          </p>
          <div className="spend-details-reference-breakdown">
            <span>
              Medsurg:{" "}
              <NumberFormat
                value={Math.round(medsurg)}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
              />
              {" (20%)"}
            </span>
            <span>
              Direct Purchases:{" "}
              <NumberFormat
                value={Math.round(directPurchases)}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
              />
              {" (60%)"}
            </span>
            <span>
              Bill-Only:{" "}
              <NumberFormat
                value={Math.round(billOnly)}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
              />
              {" (20%)"}
            </span>
          </div>
        </div>

        <div className="table">{rows}</div>
        <Total
          total={{
            title: "Annual Supply Spend to be Managed by PAR",
            value: (
              <NumberFormat
                allowEmptyFormatting
                value={annualSupplySpendTotal}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
              />
            ),
          }}
          subtotals={[
            {
              title: "Annual Medsurg Spend to be Managed by PAR",
              value: (
                <NumberFormat
                  allowEmptyFormatting
                  value={annualMedsurgSpendTotal}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"$"}
                />
              ),
            },
            {
              title: "Annual Direct Purchase Spend to be Managed by PAR",
              value: (
                <NumberFormat
                  allowEmptyFormatting
                  value={annualDirectPurchaseSpendTotal}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"$"}
                />
              ),
            },
            {
              title: "Annual Bill-Only Spend to be Managed by PAR",
              value: (
                <NumberFormat
                  allowEmptyFormatting
                  value={annualBillOnlySpendTotal}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"$"}
                />
              ),
            },
            {
              title: "Total Monthly Call Downs (all locations)",
              value: (
                <NumberFormat
                  allowEmptyFormatting
                  // The facility inputs capture *daily* call downs; convert the
                  // summed daily figure to a monthly total (~30 days/month).
                  value={monthlyCallDownsTotal * DAYS_PER_MONTH}
                  displayType={"text"}
                  thousandSeparator={true}
                />
              ),
            },
            ...(existingMode
              ? [
                  {
                    title: "Existing Scale Count",
                    value: (
                      <NumberFormat
                        allowEmptyFormatting
                        value={scaleSplitTotals.existingScales}
                        displayType={"text"}
                        thousandSeparator={true}
                      />
                    ),
                  },
                  {
                    title: "New Scales in This Deal",
                    value: (
                      <NumberFormat
                        allowEmptyFormatting
                        value={scaleSplitTotals.newScales}
                        displayType={"text"}
                        thousandSeparator={true}
                      />
                    ),
                  },
                ]
              : []),
            {
              title: "Total Scale Count",
              value: (
                <NumberFormat
                  allowEmptyFormatting
                  value={existingMode ? scaleSplitTotals.totalScales : scaleCountTotal}
                  displayType={"text"}
                  thousandSeparator={true}
                />
              ),
            },
          ]}
        />

        {npsr > 0 && (() => {
          const allocatedSpend = isSameDayIncluded
            ? annualSupplySpendTotal + annualBillOnlySpendTotal
            : annualSupplySpendTotal;
          const allocationPct = estimatedAddressableSpend > 0
            ? Math.round((allocatedSpend / estimatedAddressableSpend) * 100)
            : 0;
          const spendLabel = isSameDayIncluded
            ? "Medsurg + Direct Purchases + Bill-Only"
            : "Medsurg + Direct Purchases";
          return (
            <div className="spend-allocation-summary">
              <div className="spend-allocation-title">
                Total Spend to be Managed ({spendLabel})
              </div>
              <div className="spend-allocation-row">
                <span className="spend-allocation-label">Allocated in this ROI:</span>
                <span className="spend-allocation-value">
                  <NumberFormat
                    value={Math.round(allocatedSpend)}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={"$"}
                  />
                </span>
              </div>
              <div className="spend-allocation-row">
                <span className="spend-allocation-label">Estimated addressable spend:</span>
                <span className="spend-allocation-value">
                  <NumberFormat
                    value={Math.round(estimatedAddressableSpend)}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={"$"}
                  />
                </span>
              </div>
              <div className={`spend-allocation-row spend-allocation-pct ${allocationPct < 50 ? "low" : allocationPct < 80 ? "medium" : ""}`}>
                <span className="spend-allocation-label">Spend allocated:</span>
                <span className="spend-allocation-value spend-allocation-pct-value">
                  {allocationPct}%
                </span>
              </div>
              {allocationPct < 80 && (
                <div className="spend-allocation-warning">
                  ⚠ Only {allocationPct}% of estimated addressable spend is included. A higher allocation typically produces a stronger ROI.
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </Slide>
  );
};
